import { extractImageFrames, clampTo } from '../../tools';
import type { KieModelConfig } from '../types';

export const alibabaConfigs: Record<string, KieModelConfig> = {
    'wan-3.0': {
        // https://kie.ai/wan3.0-video
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'wan/3-0-video',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 首帧 URL
                        first_frame_url: firstImage!,
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'wan/3-0-video',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 首帧 URL
                        first_frame_url: firstImage!,
                        // 尾帧 URL
                        last_frame_url: lastImage!,
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                const images = (imagePrompt ?? []).filter(Boolean).slice(0, 10);
                const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 5);
                const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 5);
                return {
                    model: 'wan/3-0-video',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 参考 URL
                        reference_image_urls: images,
                        reference_video_urls: videos,
                        reference_audio_urls: audios,
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            // 文生视频模式
            } else {
                return {
                    model: 'wan/3-0-video',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            }
        },
    },
    'wan-3.0-prime': {
        // https://kie.ai/wan3.0-video-prime
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'wan/3-0-video-prime',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 首帧 URL
                        first_frame_url: firstImage!,
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'wan/3-0-video-prime',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 首帧 URL
                        first_frame_url: firstImage!,
                        // 尾帧 URL
                        last_frame_url: lastImage!,
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                const images = (imagePrompt ?? []).filter(Boolean).slice(0, 10);
                const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 5);
                const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 5);
                return {
                    model: 'wan/3-0-video-prime',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 参考 URL
                        reference_image_urls: images,
                        reference_video_urls: videos,
                        reference_audio_urls: audios,
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            // 文生视频模式
            } else {
                return {
                    model: 'wan/3-0-video-prime',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                        duration: clampTo(duration || 5, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['480P', '720P', '1080P'] as const, '720P'),
                        // 可选参数
                        audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            }
        },
    },
};